import { createTheme } from '@mui/material/styles'

const brandColors = {
  primaryDark: '#0D0D0D', // Deep-Space
  primaryLight: '#FAEFED', // Stardust
  plutoYellow: '#FFF200', // Plutonium
  charcoal: '#161616',
  graphite: '#1F1F1F',
  parchment: '#FDF9F6',
  mist: 'rgba(250, 239, 237, 0.72)',
}

const fontFaces = [
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 100,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Thin.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 300,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Light.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Regular.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 500,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Medium.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-SemiBold.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Bold.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 800,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-ExtraBold.otf") format("opentype")',
  },
  {
    fontFamily: 'Pluto Sans',
    fontStyle: 'normal',
    fontWeight: 900,
    fontDisplay: 'swap',
    src: 'url("/fonts/PlutoTVSansV10-Black.otf") format("opentype")',
  },
]

const fontStack = '"Pluto Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'

const fallbackBackgroundGradient =
  'radial-gradient(circle at 20% 20%, rgba(255,242,0,0.18) 0%, transparent 45%), radial-gradient(circle at 80% 0%, rgba(250,239,237,0.10) 0%, transparent 40%)'

const cardBackgroundGradient =
  'linear-gradient(135deg, rgba(255,242,0,0.08), rgba(250,239,237,0.02))'

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandColors.plutoYellow,
      light: '#FFF566',
      dark: '#C7B700',
      contrastText: brandColors.primaryDark,
    },
    secondary: {
      main: brandColors.primaryLight,
      light: brandColors.parchment,
      dark: '#D3C5C2',
      contrastText: brandColors.primaryDark,
    },
    background: {
      default: brandColors.primaryDark,
      paper: brandColors.charcoal,
    },
    text: {
      primary: brandColors.primaryLight,
      secondary: brandColors.mist,
    },
    success: {
      main: '#4CEAAC',
    },
    warning: {
      main: brandColors.plutoYellow,
    },
  },
  typography: {
    fontFamily: fontStack,
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
    subtitle2: {
      fontWeight: 500,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.7,
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    button: {
      fontFamily: fontStack,
      fontWeight: 900,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    overline: {
      fontWeight: 900,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 20,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--pluto-background-image': 'none',
          '--pluto-card-background': 'none',
        },
        '@font-face': fontFaces,
        body: {
          backgroundColor: brandColors.primaryDark,
          backgroundImage: `var(--pluto-background-image, ${fallbackBackgroundGradient})`,
          color: brandColors.primaryLight,
          fontFamily: fontStack,
          minHeight: '100vh',
        },
        '*::selection': {
          backgroundColor: 'rgba(255,242,0,0.35)',
          color: brandColors.primaryDark,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: 28,
          paddingBlock: 14,
        },
        containedPrimary: {
          boxShadow: '0 16px 40px rgba(255,242,0,0.25)',
        },
        containedSecondary: {
          boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primaryDark,
          backgroundImage: `var(--pluto-card-background, ${cardBackgroundGradient})`,
          border: '1px solid rgba(255,242,0,0.12)',
          boxShadow: '0 30px 60px rgba(0,0,0,0.65)',
          backdropFilter: 'blur(18px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.graphite,
          color: brandColors.primaryLight,
          fontFamily: fontStack,
          fontWeight: 900,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        },
      },
    },
  },
})

export default theme